import { IconCalendar } from "@tabler/icons-react";
import {
  DatePicker as AriaDatePicker,
  type DatePickerProps as AriaDatePickerProps,
  type DateValue,
  type ValidationResult,
} from "react-aria-components";

import { cn } from "@beweegbaas/ui/lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { DateInput } from "./date-input";
import { FieldDescription, FieldError, FieldLabel } from "./field";
import { InputGroup } from "./input-group";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface DatePickerProps<T extends DateValue> extends AriaDatePickerProps<T> {
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  fieldGroupClassName?: string;
  className?: string;
}

export function DatePicker<T extends DateValue>({
  label,
  description,
  errorMessage,
  fieldGroupClassName,
  className,
  ...props
}: DatePickerProps<T>) {
  return (
    <AriaDatePicker {...props} className={cn("group flex flex-col gap-1", className)}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <InputGroup className={cn("w-auto min-w-[208px]", fieldGroupClassName)}>
        <DateInput className="min-w-[150px] flex-1" />
        <Popover>
          <PopoverTrigger
            render={
              <Button
                size="icon"
                variant="secondary"
                className="mr-1.5 h-6 w-4 rounded-lg outline-offset-0"
                nativeButton={false}
              >
                <IconCalendar aria-hidden className="h-4 w-4" />
              </Button>
            }
          />
          <PopoverContent side="bottom" className="w-auto">
            <Calendar />
          </PopoverContent>
        </Popover>
      </InputGroup>
      {description && <FieldDescription>{description}</FieldDescription>}
      {errorMessage && typeof errorMessage === "string" && <FieldError>{errorMessage}</FieldError>}
    </AriaDatePicker>
  );
}
